import React, { useEffect, useState } from 'react';

import { useDarkMode } from '@/hooks/useDarkMode';
import constants from '@/theme/exportedVariables.module.scss';
import {
	RadialBarDatum,
	RadialBarSerie,
	ResponsiveRadialBar,
} from '@nivo/radial-bar';
import { useTimeoutFn } from 'react-use';

export type RadialBarChartData = RadialBarSerie<RadialBarDatum>[];

export type RadialBarChartProps = {
	data: RadialBarChartData;
	maxValue?: number;
	wrapperClassName?: string;
	animationDelay?: number;
	colors?: string[];
};

export const RadialBarChart: React.FC<RadialBarChartProps> = ({
	data: originalData,
	maxValue,
	wrapperClassName,
	animationDelay,
	colors,
}) => {
	const { isDarkModeOn } = useDarkMode();
	const [animationComplete, setAnimationComplete] = useState(false);
	// Needed for the initial animation
	// It will assign the data with y: 0 for all data fields
	// and when the useTimeoutFn runs it will set it to the initial values
	const [data, setData] = useState<RadialBarChartData>(
		originalData.map((idObj) => ({
			...idObj,
			data: idObj.data.map((dataObj) => ({ ...dataObj, y: 0 })),
		})),
	);
	// Second part of the animation logic
	useTimeoutFn(() => {
		setData(originalData);
		setAnimationComplete(true);
	}, animationDelay || 200);

	useEffect(() => {
		if (animationComplete) {
			setData(originalData);
		}
	}, [originalData, animationComplete]);

	return (
		<div className={wrapperClassName}>
			<ResponsiveRadialBar
				data={data}
				valueFormat=">-.2f"
				padding={0.2}
				innerRadius={0.15}
				cornerRadius={45}
				endAngle={360}
				maxValue={maxValue}
				enableRadialGrid={false}
				enableCircularGrid={false}
				tracksColor={isDarkModeOn ? '#36395a' : '#f5f5fa'}
				isInteractive={false}
				colors={colors}
				radialAxisStart={{
					tickSize: 5,
					tickPadding: 5,
					tickRotation: 0,
					tickComponent: () => null,
				}}
				circularAxisOuter={{
					tickComponent: () => null,
				}}
			/>
		</div>
	);
};
